import { makeStyles } from '@material-ui/styles'
import ServiceUnhealthyInfo from 'app/plugins/openstack/components/common/ServiceUnhealthyInfo'
import { listFlavors } from 'app/plugins/openstack/components/flavors/actions'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { listNetworks } from 'app/plugins/openstack/components/networks/actions'
import { networksSelector } from 'app/plugins/openstack/components/networks/selectors'
import { listStoragePools } from 'app/plugins/openstack/components/storage/pools/actions'
import { storagePoolsSelector } from 'app/plugins/openstack/components/storage/pools/selectors'
import { RootState, useAppSelector } from 'app/store'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isAirgapped } from 'core/utils/helpers'
import AddNodeGroups from 'k8s/components/capacity-and-health/AddNodeGroups'
import {
  NodePoolErrors,
  UNSUPPORTED_VM_FLAVORS,
} from 'k8s/components/cluster/deployment/form-components/ServerConfigurationFields'
import { listKaapiClusters } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from 'k8s/components/kaapi/clusters/selectors'
import { createKaapiKubeadmConfigTemplateBody } from 'k8s/components/kaapi/kubeadm-config-templates/action-helpers'
import { createKaapiKubeadmConfigTemplate } from 'k8s/components/kaapi/kubeadm-config-templates/actions'
import { createKaapiMachineDeploymentBody } from 'k8s/components/kaapi/machine-deployment/action-helpers'
import {
  createKaapiMachineDeployment,
  listKaapiMachineDeployments,
} from 'k8s/components/kaapi/machine-deployment/actions'
import { createKaapiOpenStackMachineTemplateBody } from 'k8s/components/kaapi/openstack-machine-templates/action-helpers'
import { createKaapiOpenStackMachineTemplate } from 'k8s/components/kaapi/openstack-machine-templates/actions'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { isNilOrEmpty } from 'utils/fp'
import { v4 as uuidv4 } from 'uuid'
import { getDUInternalIP } from '../cluster/deployment/form-components/helpers'
import { listKaapiConfigMaps } from '../kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from '../kaapi/config-maps/selectors'

const validateNodePools = (nodePools) => {
  const errors = {}
  nodePools.forEach(({ id, flavor, replicas }) => {
    if (!flavor || !replicas) {
      errors[id] = errors[id] || {}
      if (!flavor) {
        errors[id]['flavor'] = 'A VM flavor selection is required.'
      }
      if (!replicas) {
        errors[id]['replicas'] = 'The value must be greater or equal than 1'
      }
    }
  })
  return errors
}

const initialValues = {
  nodePools: [{ id: 1 }],
  openstackCluster: '',
  sshKey: '',
  imageName: '',
  k8sVersion: '',
}

const AddNodeGroupsModal = (props) => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)

  const { updatePrefs: updateK8sPluginGlobalParams } = useScopedPreferences('k8sPluginGlobalParams')
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [nodePoolErrors, setNodePoolErrors] = useState<NodePoolErrors>({})
  const [error, setError] = useState(null)

  // Get cluster name from global preferences
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')
  const clusterName: string = useMemo(() => k8sPluginGlobalPerfs?.cluster as string, [
    k8sPluginGlobalPerfs.cluster,
  ])
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()

  const isAirgappedEnv = isAirgapped()

  const { loading: loadingKaapiClusters } = useListAction(listKaapiClusters)
  const clusters = useSelector(kaapiClustersOverviewSelector)

  const clusterDetails = useMemo(() => {
    return clusters?.find((cluster) => cluster?.metadata?.name === clusterName)
  }, [clusters, clusterName])

  // Fetch existing Machine Deployments
  const { reload: reloadMachineDeployments } = useListAction(listKaapiMachineDeployments)

  // VM Flavors
  const { loading: loadingVmFlavors } = useListAction(listFlavors, {
    params,
  })
  // Filter out unsupported VM Flavors
  const vmFlavors = useAppSelector(flavorsSelector)?.filter(
    (vm) => !UNSUPPORTED_VM_FLAVORS.includes(vm?.name) && vm?.vcpus > 0 && vm?.ram > 0, // Filter out unsupported VM Flavors && hotplug flavors
  )

  // Storage Pools
  const { loading: loadingStoragePools } = useListAction(listStoragePools)
  const storagePools = useAppSelector(storagePoolsSelector)

  // Networks
  const { loading: loadingNetworks } = useListAction(listNetworks)
  const allNetworks = useAppSelector(networksSelector)
  // Filter networks by tenant
  const networksInTenant = useMemo(() => {
    return allNetworks?.filter((network) => {
      return network?.project_id === currentTenant || network?.shared
    })
  }, [allNetworks, currentTenant])

  // Load Config Maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const duInternalIP = getDUInternalIP(configMaps)

  // Create Actions
  const {
    update: createKubeadmConfigTemplate,
    updating: creatingKubeadmConfigTemplate,
    error: createKubeadmConfigTemplateError,
  } = useUpdateAction(createKaapiKubeadmConfigTemplate)
  const {
    update: createOpenStackMachineTemplate,
    updating: creatingOpenStackMachineTemplate,
    error: createOpenStackMachineTemplateError,
  } = useUpdateAction(createKaapiOpenStackMachineTemplate)
  const {
    update: createMachineDeployment,
    updating: creatingMachindeDeployment,
    error: createMachineDeploymentError,
  } = useUpdateAction(createKaapiMachineDeployment)

  const hasError =
    !!createKubeadmConfigTemplateError ||
    !!createOpenStackMachineTemplateError ||
    !!createMachineDeploymentError ||
    !!error

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const errorObj =
      createKubeadmConfigTemplateError ||
      createOpenStackMachineTemplateError ||
      createMachineDeploymentError ||
      error

    setError({
      title: errorObj?.title || 'Error creating cluster',
      message:
        errorObj?.message || 'An error occurred while creating the cluster. Please try again.',
    })
  }, [
    hasError,
    createKubeadmConfigTemplateError,
    createOpenStackMachineTemplateError,
    createMachineDeploymentError,
    error,
  ])

  const handleSubmit = async () => {
    setError(null)

    if (isAirgappedEnv && !duInternalIP) {
      setError({
        title: 'Error',
        message: 'Something went wrong, please try again. Tenant may not be configured correctly',
      })
      return
    }

    const nodePoolErrors = validateNodePools(params.nodePools)
    setNodePoolErrors(nodePoolErrors)
    if (!isNilOrEmpty(nodePoolErrors) || !params.imageName || !params.openstackCluster) {
      return
    }

    // For each nodepool, create KubeadmConfigTemplate, MachineDeployment and OpenStackMachineTemplate
    for (const [idx, nodePool] of params.nodePools.entries()) {
      // Generate a short UUID
      const shortUUID = uuidv4().slice(0, 8)
      const name = `${clusterName}-${shortUUID}`

      // Create OpenStackMachineTemplate
      const openStackMachineTemplateBody = createKaapiOpenStackMachineTemplateBody({
        name,
        namespace,
        flavor: nodePool.flavor,
        clusterName,
        storage: nodePool.storage,
        network: nodePool.network,
        subnet: nodePool.subnet,
        imageName: params.imageName,
        sshKeyName: params.sshKey,
        allowedAddressesPools:
          clusterDetails?.clusterAddon?.spec?.clusterSpec?.firewallConfig?.allowedAddressPools ||
          [],
      })
      await createOpenStackMachineTemplate({ namespace, body: openStackMachineTemplateBody })

      // Create MachineDeployment
      const machineDeploymentCreateBody = createKaapiMachineDeploymentBody({
        name,
        namespace,
        clusterName,
        replicas: nodePool.replicas,
        configTemplateRefName: clusterName,
        infrastructureRefName: name,
        k8sVersion: props?.k8sVersion,
        autoscaling: nodePool.autoscaling,
        openstackCluster: params.openstackCluster,
      })
      await createMachineDeployment({ namespace, body: machineDeploymentCreateBody })
    }

    if (!hasError) {
      // Reload the machine deployments and machines
      reloadMachineDeployments(true, false)
      props.onClose()
    }
  }

  const submitting =
    creatingKubeadmConfigTemplate || creatingOpenStackMachineTemplate || creatingMachindeDeployment

  const handleNodePoolChange = (id) => (newValues) => {
    const updatedNodePools = params.nodePools.map((nodePool) => {
      if (nodePool.id === id) {
        return { ...nodePool, ...newValues }
      }
      return nodePool
    })
    getParamsUpdater('nodePools')(updatedNodePools)
  }

  return (
    <ModalForm
      {...props}
      open
      title="Add Node Group"
      maxWidth={820}
      className={classes.modal}
      onSubmit={handleSubmit}
      submitTitle="Add Node Group"
      error={error}
      submitting={submitting}
      onClose={props.onClose}
      data-testid="add-node-group-modal"
    >
      <ServiceUnhealthyInfo
        action="Node Group Creation"
        hypervisor
        imageLibrary
        blockStorage
        network
      />
      <AddNodeGroups
        nodePool={params.nodePools[0]}
        params={params}
        vmFlavors={vmFlavors}
        storagePools={storagePools}
        networks={networksInTenant}
        showDeleteButton={false}
        onDeleteNodePool={() => {}}
        errors={nodePoolErrors[params.nodePools[0].id]}
        showOpenstackCluster
        getParamsUpdater={getParamsUpdater}
        onChange={handleNodePoolChange(params.nodePools[0].id)}
        k8sVersion={props?.k8sVersion}
      />
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
}))

export default AddNodeGroupsModal
